import {
  CircularProgress,
  Grid,
  Tooltip,
  withStyles,
  WithStyles,
  withWidth
} from '@material-ui/core'
import { WithWidth } from '@material-ui/core/withWidth'
import { ObjectFitProperty } from 'csstype'
import i18n from 'i18next'
import DeleteCircleIcon from 'mdi-react/DeleteCircleIcon'
import { Component, SyntheticEvent } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { isSmallScreen } from '../../../libs/screenSize'
import { BucketObject } from '../../../services/storage/types'
import { ApartmentMedia } from '../../../types/apartments'
import { ImageFileWithObjectURL } from '../../../types/files'

const styles = () => ({
  root: {
    maxWidth: '100%',
    padding: '0px 0px 25px 0px',
    '& .slick-prev, & .slick-next': {
      width: 42,
      height: 42,
      zIndex: 2
    },
    '& .slick-prev': {
      left: 14
    },
    '& .slick-next': {
      right: 14
    },
    '& .slick-prev:before, & .slick-next:before': {
      fontSize: '42px',
      color: '#eaeae9'
    }
  },
  removeIcon: {
    zIndex: '99999' as any,
    position: 'absolute' as any,
    top: '0px',
    right: '10px',
    cursor: 'pointer',
    paddingTop: 8
  },
  imageContainer: {
    position: 'relative' as any
  }
})

export const isBucketObject = (
  file: BucketObject | ImageFileWithObjectURL
): file is BucketObject => (file as BucketObject)?.url !== undefined

type MediaFile = BucketObject | ImageFileWithObjectURL

interface MediaProps extends WithStyles, WithWidth {
  files: MediaFile[] | undefined
  media?: ApartmentMedia
  withDelete?: { deleteMedia: (arg: any) => any }
  slidesToShow?: number
  t: i18n.TFunction
  /** css height property */
  imageHeight?: string
  /** css width property */
  imageWidth?: string
  /**  objectFit property */
  imageFit?: ObjectFitProperty
  /** css background property */
  imageBackground?: string
  fullWidthImages?: boolean
  showDots?: boolean
}

interface MediaSliderState {
  orientations: Record<string, 'portrait' | 'landscape'>
}

class MediaSlider extends Component<MediaProps, MediaSliderState> {
  state: MediaSliderState = {
    orientations: {}
  }

  slideAmount = (slidesToShow?: number) => slidesToShow || 1

  slidesToShow = () => {
    const slideAmount = this.slideAmount(this.props.slidesToShow)
    const isNormalScreen = !isSmallScreen(this.props.width)
    const slidesToShow =
      this.props.files &&
      this.props.files.length >= slideAmount &&
      isNormalScreen
        ? slideAmount
        : 1

    return slidesToShow
  }

  handleImageLoad =
    (fileKey: string) =>
    (event: SyntheticEvent<HTMLImageElement>): void => {
      const img = event.currentTarget
      const orientation =
        img.naturalHeight > img.naturalWidth ? 'portrait' : 'landscape'
      this.setState((prev) => {
        if (prev.orientations[fileKey] === orientation) {
          return null
        }
        return {
          orientations: {
            ...prev.orientations,
            [fileKey]: orientation
          }
        }
      })
    }

  render() {
    const {
      files,
      media,
      slidesToShow,
      withDelete,
      t,
      classes,
      imageHeight,
      imageWidth,
      imageFit,
      imageBackground,
      fullWidthImages,
      showDots = true
    } = this.props
    const containerHeight = isSmallScreen(this.props.width) ? 420 : 655
    const settings = {
      dots: showDots,
      infinite: files && files.length >= this.slideAmount(slidesToShow),
      speed: 500,
      slidesToShow: this.slidesToShow(),
      slidesToScroll: this.slidesToShow(),
      arrows: true,
      adaptiveHeight: true
    }

    let sortedFiles: (BucketObject | ImageFileWithObjectURL)[] = files || []
    if (Array.isArray(media) && Array.isArray(files) && media.length) {
      sortedFiles = media
        .map((mediaItem) => {
          return (
            files.find((file) => file.key === mediaItem.key) ||
            ({} as BucketObject)
          )
        })
        .filter((file) => !!file.key)
    }

    return (
      <Grid className={classes.root}>
        {sortedFiles && sortedFiles.length > 0 ? (
          <Slider {...settings}>
            {sortedFiles.map((file) => (
              <Grid key={file.key} className={classes.imageContainer}>
                {withDelete && (
                  <Tooltip title={`${t('apartment.mediaUpload.delete')}`}>
                    <span
                      className={classes.removeIcon}
                      onClick={() =>
                        withDelete && withDelete.deleteMedia(file.key)
                      }
                    >
                      <DeleteCircleIcon size={40} color='red' />
                    </span>
                  </Tooltip>
                )}
                <div
                  style={{
                    width: '100%',
                    height: `${containerHeight}px`,
                    display: 'flex'
                  }}
                >
                  {(() => {
                    const orientation = this.state.orientations[file.key]
                    const computedFit =
                      imageFit ||
                      (fullWidthImages
                        ? orientation
                          ? orientation === 'portrait'
                            ? 'contain'
                            : 'cover'
                          : 'cover'
                        : 'contain')
                    return (
                      <>
                        {!orientation && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 2
                            }}
                          >
                            <CircularProgress size={48} style={{ color: '#5d305c' }} />
                          </div>
                        )}
                        <img
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: computedFit,
                            background: imageBackground || undefined
                          }}
                          className={classes.image}
                          src={isBucketObject(file) ? file.url : file.key}
                          alt=''
                          decoding='async'
                          onLoad={this.handleImageLoad(file.key)}
                        />
                      </>
                    )
                  })()}
                </div>
              </Grid>
            ))}
          </Slider>
        ) : (
          <div
            style={{
              width: '100%',
              height: `${containerHeight}px`,
              backgroundColor: '#e6e9ee'
            }}
          />
        )}
      </Grid>
    )
  }
}

export default withStyles(styles)(withWidth()(MediaSlider))
